import { supabase } from '../../lib/supabase'
import { getAccounts, type Account } from './accountStore'
import { AUDIT_CATEGORIES, accountName, describeAuditEvent, type AuditLookups, type AuditRow, type DescribedEvent } from './auditEvents'

/**
 * The audit log an administrator reads (audit_events_read_administrator):
 * every event in muster.audit_events, newest first, in pages, narrowed by the
 * kind of event, who did it, and when. The log is never edited through the
 * application; this only reads it.
 */
export const AUDIT_PAGE_SIZE = 25

export type AuditFilters = {
  category: string // a key of AUDIT_CATEGORIES, or 'all'
  actor: string // an account id, 'system' for an event with no acting user, or 'all'
  from: string // an ISO date, or ''
  to: string // an ISO date, or ''
}

export const NO_FILTERS: AuditFilters = { category: 'all', actor: 'all', from: '', to: '' }

export type AuditEntry = DescribedEvent & {
  id: string
  occurredAt: string
  actorName: string
  previous: Record<string, unknown> | null
  next: Record<string, unknown> | null
  reason: string | null
}

export type AuditLog = { entries: AuditEntry[]; total: number }

// The start of a local calendar day as a timestamp, so a "from" and "to" date
// mean the days the administrator sees, not UTC days.
function startOfDay(iso: string, addDays = 0): string {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(year, month - 1, day + addDays).toISOString()
}

/**
 * The accounts, employees, and teams the events point at, read once so every
 * page of the log can name them. At this pilot's size each is a short list.
 */
export async function getAuditLookups(): Promise<{ lookups: AuditLookups; accounts: Account[] }> {
  const [accounts, employeesResult, teamsResult] = await Promise.all([
    getAccounts(),
    supabase.from('employees').select('id, display_name'),
    supabase.from('teams').select('id, name'),
  ])
  const employees = (employeesResult.data ?? []) as { id: string; display_name: string }[]
  const teams = (teamsResult.data ?? []) as { id: string; name: string }[]
  return {
    accounts,
    lookups: {
      accounts: new Map(accounts.map((account) => [account.id, account])),
      employees: new Map(employees.map((employee) => [employee.id, employee.display_name])),
      teams: new Map(teams.map((team) => [team.id, team.name])),
    },
  }
}

/**
 * One page of the log, or null when it could not be read. Ordered by time,
 * then by id, so events written in one transaction keep a stable order from
 * page to page.
 */
export async function getAuditLog(filters: AuditFilters, page: number, lookups: AuditLookups): Promise<AuditLog | null> {
  let query = supabase
    .from('audit_events')
    .select('id, actor, action, subject_table, subject_id, previous_value, new_value, reason, occurred_at', { count: 'exact' })

  const category = AUDIT_CATEGORIES.find((candidate) => candidate.key === filters.category)
  if (category) query = query.in('action', category.actions)
  if (filters.actor === 'system') query = query.is('actor', null)
  else if (filters.actor !== 'all') query = query.eq('actor', filters.actor)
  if (filters.from) query = query.gte('occurred_at', startOfDay(filters.from))
  if (filters.to) query = query.lt('occurred_at', startOfDay(filters.to, 1))

  const first = page * AUDIT_PAGE_SIZE
  const { data, error, count } = await query
    .order('occurred_at', { ascending: false })
    .order('id', { ascending: false })
    .range(first, first + AUDIT_PAGE_SIZE - 1)
  if (error || !data) return null

  const entries = (data as unknown as AuditRow[]).map((row): AuditEntry => ({
    id: row.id,
    occurredAt: row.occurred_at,
    actorName: row.actor ? accountName(lookups.accounts.get(row.actor)) : 'The system',
    previous: row.previous_value,
    next: row.new_value,
    reason: row.reason,
    ...describeAuditEvent(row, lookups),
  }))
  return { entries, total: count ?? entries.length }
}
